import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowRight, MessageCircle, ShoppingBag } from "lucide-react";
import { whatsappOrderUrl } from "@/lib/ordering";
import { useCart } from "@/lib/CartContext";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImg = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

const LINES = [
  { key: "all", label: "All" },
  { key: "classic", label: "Classic" },
  { key: "labour", label: "Eve Labour" },
  { key: "fluffy", label: "Fluffy" },
];

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [tab, setTab] = useState("all");
  const { addItem } = useCart();

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data.products || [])).catch(() => {});
  }, []);

  function handleAdd(p) {
    addItem(p, 1);
    toast.success(`${p.name} added to cart`);
  }

  const filtered = tab === "all" ? products : products.filter((p) => p.line === tab);

  return (
    <div data-testid="shop-page" className="bg-white">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 md:pt-28 pb-14 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-6">
          <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">Shop our pads</p>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl mt-3 tracking-tighter">
            Clean, comfortable, confidently Ghanaian.
          </h1>
          <p className="mt-5 text-faytex-muted max-w-xl">
            A pad for every chapter — from your first cycle to motherhood and beyond. Made in our Kpone Factory with clinically trusted materials.
          </p>
        </div>
        <div className="lg:col-span-6">
          <div className="rounded-3xl overflow-hidden shadow-hover bg-white">
            <img
              src="/product-range.webp"
              alt="The full Faytex product range — Classic, Eve Labour Pad, Fluffy Multi-Pack and Fluffy Extra Long"
              className="w-full h-auto object-cover"
            />
          </div>
        </div>
      </section>

      {/* Our Range Philosophy */}
      <section
        data-testid="our-range"
        className="bg-faytex-pinkLight/40 border-y border-faytex-pinkLight"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
          <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">Our Range</p>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-3 tracking-tight max-w-3xl">
            One pad cannot meet every need.
          </h2>
          <div className="mt-8 space-y-5 text-faytex-muted leading-relaxed text-lg">
            <p>
              What makes Faytex products different is rooted in a simple understanding: women
              aren&rsquo;t all the same, and neither are their needs.
            </p>
            <p>
              Those needs change throughout a woman&rsquo;s life and even throughout a single
              menstrual cycle. Faytex has grown organically alongside those changing needs.
            </p>
            <p>
              We started with our original <strong className="text-faytex-ink">Faytex Classic Pad</strong>,
              a product trusted not only by women for everyday menstrual care but also by
              hospitals and clinics for maternity care. As those needs evolved, we developed the
              <strong className="text-faytex-ink"> Eve Labour Pad</strong>, specifically for labour and
              delivery. Later came our <strong className="text-faytex-ink">Faytex Fluffy</strong> range,
              including the Multi-Pack, built around a simple understanding: one type of pad cannot
              meet every need throughout a woman&rsquo;s period.
            </p>
            <p className="font-heading text-2xl text-faytex-ink leading-snug">
              We don&rsquo;t believe in a one-size-fits-all approach to women&rsquo;s care.
            </p>
            <p>
              Our range has evolved by understanding how women&rsquo;s needs change — and
              developing products for the different moments when they need them.
            </p>
          </div>

          {/* Four product cards */}
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                name: "Faytex Classic",
                tag: "The product that built the Faytex name.",
                accent: "border-faytex-pink bg-white",
                body: [
                  "Formerly known as Faytex Regular, this is our original sanitary pad and the product on which the Faytex name was built. \u201CRegular\u201D originally referred to the size of the pad.",
                  "Designed for menstrual protection, it gradually also became a trusted choice for maternity care. Today, Faytex Classic remains a reliable everyday menstrual pad while continuing to be widely used in hospitals and polyclinics across Southern Ghana for labour and post-delivery care.",
                ],
              },
              {
                name: "Eve Labour Pad",
                tag: "Created specifically for maternity care.",
                accent: "border-blue-200 bg-white",
                body: [
                  "The Eve Labour Pad was developed specifically for maternity care, responding to the needs of hospitals and healthcare professionals, particularly in the Northern and Ashanti Regions, where dedicated labour pads were increasingly being used.",
                  "Unlike a menstrual pad, it does not contain an adhesive strip or release paper because it is not designed to be worn on underwear. Instead, it is purpose-built to provide soft, dependable absorbency during labour and immediately after childbirth, supporting both healthcare professionals and new mothers when it matters most.",
                ],
              },
              {
                name: "Faytex Fluffy Multi-Pack",
                tag: "Different protection for different days.",
                accent: "border-emerald-200 bg-white",
                body: [
                  "No woman\u2019s period is the same from start to finish, so we asked: why should every pad be?",
                  "The Faytex Fluffy Multi-Pack contains 16 pads across three absorbencies: 4 Light Flow, 6 Heavy Flow and 6 Extra Long Night Pads. This gives women the flexibility to choose the right level of protection as their flow changes throughout their period.",
                  "It is a convenient and cost-effective solution designed so that one pack can comfortably see most women through their entire period.",
                ],
              },
              {
                name: "Faytex Fluffy Extra Long",
                tag: "Maximum protection when it\u2019s needed most.",
                accent: "border-orange-200 bg-white",
                body: [
                  "The Faytex Fluffy Extra Long is designed for heavier-flow days and overnight use, providing additional length, coverage and absorbency.",
                  "With 10 extra-long pads per pack, it helps reduce the worry of leaks while providing greater comfort and confidence, whether during the day or overnight. More coverage means greater peace of mind and a more comfortable night\u2019s sleep.",
                ],
              },
            ].map((prod) => (
              <article
                key={prod.name}
                data-testid={`range-card-${prod.name.replace(/\s+/g, "-").toLowerCase()}`}
                className={`rounded-3xl border-2 ${prod.accent} p-7 shadow-soft`}
              >
                <h3 className="font-heading text-2xl md:text-3xl leading-tight">{prod.name}</h3>
                <p className="mt-2 text-sm text-faytex-pink font-semibold uppercase tracking-widest">
                  {prod.tag}
                </p>
                <div className="mt-4 space-y-3 text-sm text-faytex-muted leading-relaxed">
                  {prod.body.map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              </article>
            ))}
          </div>

          {/* Summary */}
          <div className="mt-14 rounded-3xl bg-faytex-ink text-white p-8 md:p-10">
            <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">In summary</p>
            <ul className="mt-5 space-y-3 text-white/90">
              <li>
                <strong className="text-white">Faytex Classic:</strong> The product that built the Faytex name.
              </li>
              <li>
                <strong className="text-white">Eve Labour Pad:</strong> Purpose-built for maternity care.
              </li>
              <li>
                <strong className="text-white">Fluffy Multi-Pack:</strong> Different protection for the changing days of a woman&rsquo;s period.
              </li>
              <li>
                <strong className="text-white">Fluffy Extra Long:</strong> Maximum coverage and protection when it&rsquo;s needed most.
              </li>
            </ul>
            <p className="mt-6 font-heading text-xl md:text-2xl text-faytex-pink leading-snug">
              As women&rsquo;s needs change, Faytex evolves with them.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="shop-tabs" className="bg-faytex-pinkLight/60 p-1 rounded-full mb-10 flex-wrap h-auto">
            {LINES.map((l) => (
              <TabsTrigger
                key={l.key}
                value={l.key}
                data-testid={`shop-tab-${l.key}`}
                className="rounded-full px-5 py-2 text-sm font-medium data-[state=active]:bg-faytex-pink data-[state=active]:text-white"
              >
                {l.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={tab}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="shop-grid">
              {filtered.map((p) => (
                <article
                  key={p.id}
                  data-testid={`product-card-${p.id}`}
                  className="group rounded-2xl border border-gray-100 bg-white shadow-soft hover:shadow-hover transition-all overflow-hidden flex flex-col"
                >
                  <Link
                    to={`/shop/${p.id}`}
                    data-testid={`product-link-${p.id}`}
                    className="block"
                  >
                    <div className="h-1.5 w-full" style={{ background: p.color }} />
                    <div className="aspect-square bg-faytex-pinkLight/40 flex items-center justify-center p-8 overflow-hidden">
                      <img src={resolveImg(p.image)} alt={p.name} className="max-h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  </Link>
                  <div className="p-6 flex flex-col flex-1">
                    <p className="text-[11px] uppercase tracking-widest text-faytex-muted">{p.line} • {p.absorbency}</p>
                    <Link to={`/shop/${p.id}`} className="mt-1 hover:text-faytex-pink transition-colors">
                      <h3 className="font-heading text-2xl">{p.name}</h3>
                    </Link>
                    <p className="text-sm text-faytex-muted mt-1">{p.tagline}</p>
                    {p.features?.length > 0 && (
                      <ul className="mt-4 space-y-1.5 text-sm text-faytex-ink">
                        {p.features.slice(0, 3).map((f) => (
                          <li key={f} className="flex items-start gap-2">
                            <span className="mt-1 inline-block w-1.5 h-1.5 rounded-full bg-faytex-pink" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="mt-6 flex items-center justify-between">
                      <span className="font-heading text-2xl text-faytex-pink">GH₵ {p.price_ghs}</span>
                      <span className="text-xs text-faytex-muted">{p.count}-pack</span>
                    </div>
                    <div className="mt-5 grid grid-cols-3 gap-2">
                      <button
                        onClick={() => handleAdd(p)}
                        data-testid={`product-addcart-${p.id}`}
                        className="inline-flex items-center justify-center gap-1 rounded-full bg-faytex-pink hover:bg-faytex-pinkHover text-white px-2 py-2 text-xs font-semibold transition-colors"
                      >
                        <ShoppingBag className="w-3.5 h-3.5" /> Add
                      </button>
                      <a
                        href={whatsappOrderUrl(p)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`product-whatsapp-${p.id}`}
                        className="inline-flex items-center justify-center gap-1 rounded-full bg-[#25D366] hover:brightness-95 text-white px-2 py-2 text-xs font-semibold transition"
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                      </a>
                      <Link
                        to={`/shop/${p.id}`}
                        data-testid={`product-details-${p.id}`}
                        className="inline-flex items-center justify-center rounded-full bg-faytex-ink hover:bg-faytex-pink text-white px-2 py-2 text-xs font-semibold transition-colors"
                      >
                        Details
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </section>

      {/* B2B Strip */}
      <section className="bg-faytex-pinkLight">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">Private Label / Contract Manufacturing</p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-3 tracking-tight">Manufactured in West Africa. Trusted worldwide.</h2>
            <p className="mt-5 text-faytex-muted leading-relaxed">
              Our Kpone Factory produces millions of pads each month for partners across Africa, Europe and the Americas.
              Let us build a private label line together.
            </p>
            <Link
              to="/b2b"
              data-testid="shop-b2b-cta"
              className="inline-flex items-center gap-2 mt-8 rounded-full bg-faytex-ink text-white px-6 py-3 text-sm font-semibold hover:bg-faytex-pink transition-colors"
            >
              Request a partnership <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { k: "12M+", v: "Pads per month" },
              { k: "30+",  v: "Years of heritage" },
              { k: "200+", v: "Local jobs created" },
              { k: "8",    v: "Markets exported to" },
            ].map((s) => (
              <div key={s.k} className="bg-white rounded-2xl p-6 shadow-soft text-center">
                <p className="font-heading text-3xl text-faytex-pink">{s.k}</p>
                <p className="text-xs text-faytex-muted uppercase tracking-widest mt-1">{s.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
